// useConversationList — the rail's conversation list as a remote read plus the
// durability / emptiness projections derived off it. Split out of
// useShellFoundation (size gate) without changing behaviour: called once,
// unconditionally, at the position its cells occupied in the foundation body,
// so the hook-call order is unchanged.
export { useConversationList };

import { Remote } from "@bilda/utils";
import { useState } from "react";
import type { RecentThread } from "../../components/WorkspaceLanding/WorkspaceLanding.js";
import type { ChatSession } from "../../modules/chat/model.js";
import {
    type ConversationList,
    summariesOf,
    withStartedConversation,
} from "../conversationSummary.js";
import type { ExternalError } from "../externalError.js";

const useConversationList = (
    sessionFor: (conversationId: string | null) => ChatSession,
) => {
    // The rail's conversation list as a remote read (G6): loading, failed and
    // empty are distinct cases, so a failed read never renders as "no
    // conversations yet". `conversationListSeq` is the rail's own retry signal
    // (re-reads only this list, not the whole world). `conversationSummaries`
    // is the plain projection every other reader uses.
    const [conversationList, setConversationList] = useState<
        ConversationList<RecentThread, ExternalError>
    >(Remote.notAsked);
    const [conversationListSeq, setConversationListSeq] = useState(0);
    const retryConversationList = (): void =>
        setConversationListSeq((s) => s + 1);
    const conversationSummaries = summariesOf(conversationList);
    // Patch the HELD list in place (rename / archive optimism); a list not yet
    // held has nothing to patch, so it stays as it is.
    const updateConversationSummaries = (
        f: (prev: readonly RecentThread[]) => readonly RecentThread[],
    ): void => setConversationList(Remote.map(f));
    // A just-started conversation joins the held list; with none held, re-read
    // it (the server has the started conversation and the rest).
    const noteConversationStarted = (started: RecentThread): void => {
        const next = withStartedConversation(conversationList, started);
        if (next === null) retryConversationList();
        else setConversationList(next);
    };
    // Which conversations are DURABLE (a `conversation_started` event exists
    // server-side) — the single nascency signal (hero-is-initial-conversation-
    // state §3). `listConversations` populates it on load and every
    // `startConversation` appends to it, so it is correctly reconstructed on
    // reload. A conversation NOT in this set is NASCENT: it renders as the hero
    // composer, its grounding is held in `pendingGrounding`, and its first send
    // starts it durably (chat's `start_conversation` effect). SHORTCUT (§1.1): a
    // durable conversation whose transcript has not loaded yet is momentarily
    // absent here on reload, so it renders as the hero for one `listConversations`
    // round-trip before its transcript lands — cosmetic, self-heals.
    const durableConversationIds: ReadonlySet<string> = new Set(
        conversationSummaries.map((c) => c.id),
    );
    const conversationIsDurable = (conversationId: string): boolean =>
        durableConversationIds.has(conversationId);
    // Whether a conversation renders as the HERO (empty state) rather than the
    // transcript — DERIVED from the session's transcript length (§1.1), never
    // stored. An empty conversation is nascent (durable ⟹ ≥1 message), so this is
    // the hero-vs-conversation layout discriminant.
    const conversationIsEmpty = (conversationId: string): boolean =>
        sessionFor(conversationId).conversation.length === 0;
    // Whether a DURABLE conversation's transcript has not landed yet (F76).
    // Because durable ⟹ ≥1 message, a durable conversation reading empty is one
    // whose `loadConversation` is still in flight — NOT a nascent hero. This is
    // the discriminant the centre needs so opening a durable conversation shows
    // a loading state instead of flickering the "new chat" hero until the
    // transcript arrives. A pure async-load readiness signal (like
    // `library === null`), driven by chatState as the transcript folds in — not
    // stored, not domain state.
    const conversationTranscriptPending = (conversationId: string): boolean =>
        conversationIsDurable(conversationId) &&
        conversationIsEmpty(conversationId);

    return {
        conversationList,
        setConversationList,
        conversationListSeq,
        retryConversationList,
        conversationSummaries,
        updateConversationSummaries,
        noteConversationStarted,
        conversationIsDurable,
        conversationIsEmpty,
        conversationTranscriptPending,
    };
};
